// Moteur cosmétique (code pur partagé) -- Familles de produits cosmetiques (code pur).
//
// Porte fidelement depuis le formulateur d'origine (BD-SapoPro V1) (const FAMILLES + PHASES_DEF +
// getFamille). Chaque famille active un sous-ensemble de phases et indique
// si un equilibre HLB est requis (emulsions uniquement).

import type { FamilleClef, PhaseId } from "./types";

export interface Famille {
  clef: FamilleClef;
  label: string;
  emoji: string;
  description: string;
  /** Phases pertinentes pour cette famille (les autres sont masquees). */
  phases_actives: PhaseId[];
  /** Une emulsion requiert un equilibre HLB phase huileuse / emulsifiant. */
  hlb_requis: boolean;
  /** Identifiant du protocole de fabrication associe. */
  protocole_type: string;
  /** Types de produit rattaches a cette famille. */
  types: string[];
}

export const FAMILLES: Record<FamilleClef, Famille> = {
  EMULSION: {
    clef: "EMULSION",
    label: "Emulsion (creme / lotion)",
    emoji: "\u{1F9F4}",
    description: "Phase aqueuse + phase huileuse + emulsifiant",
    phases_actives: [
      "phase_aqueuse",
      "phase_huileuse",
      "emulsifiant",
      "actifs",
      "conservateurs",
      "parfum",
    ],
    hlb_requis: true,
    protocole_type: "emulsion",
    types: [
      "Creme visage",
      "Creme corps",
      "Lotion tonique",
      "Masque visage",
      "Soin contour yeux",
      "BB Cream",
      "Deodorant creme",
      "Creme solaire",
      "Apres-soleil",
      "Fond de teint",
    ],
  },
  SERUM: {
    clef: "SERUM",
    label: "Serum / huile",
    emoji: "\u{1F4A7}",
    description: "Serum aqueux ou huile vegetale pure / melangee",
    phases_actives: [
      "phase_aqueuse",
      "phase_huileuse",
      "actifs",
      "conservateurs",
      "parfum",
    ],
    hlb_requis: false,
    protocole_type: "serum",
    types: ["Serum visage", "Huile corps", "Huile visage", "Serum contour yeux"],
  },
  GEL: {
    clef: "GEL",
    label: "Gel / shampoing / mousse",
    emoji: "\u{1FAE7}",
    description: "Base aqueuse gelifiee ou tensioactive",
    phases_actives: ["phase_aqueuse", "actifs", "conservateurs", "parfum"],
    hlb_requis: false,
    protocole_type: "gel",
    types: [
      "Gel douche",
      "Shampoing",
      "Gel nettoyant",
      "Gel coiffant",
      "Mousse nettoyante",
      "Apres-shampoing",
    ],
  },
  BAUME: {
    clef: "BAUME",
    label: "Baume / anhydre",
    emoji: "\u{1FA99}",
    description: "Beurres, cires, huiles sans eau",
    phases_actives: ["phase_huileuse", "actifs", "parfum"],
    hlb_requis: false,
    protocole_type: "baume",
    types: [
      "Baume levres",
      "Baume corps",
      "Exfoliant",
      "Demaquillant solide",
      "Beurre corporel",
    ],
  },
};

/**
 * Retrouve la famille d'un type de produit.
 * Defaut EMULSION si le type est inconnu (comportement identique au V1).
 */
export function getFamille(typeProduit: string): Famille {
  for (const famille of Object.values(FAMILLES)) {
    if (famille.types.includes(typeProduit)) return famille;
  }
  return FAMILLES.EMULSION;
}

/** Liste a plat de tous les types de produit connus, toutes familles confondues. */
export function tousLesTypes(): string[] {
  return Object.values(FAMILLES).flatMap((f) => f.types);
}

/** Indique si une phase est active (pertinente) pour un type de produit donne. */
export function phaseActive(typeProduit: string, phase: PhaseId): boolean {
  return getFamille(typeProduit).phases_actives.includes(phase);
}
